import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { translations, Lang, TranslationDict } from './translations';

export type { TranslationDict };

// REVAMP: simple 3-language i18n (English / 繁體中文 / 简体中文).
// Default is auto-detected from the browser language; the user can override it
// with the switcher (persisted in localStorage). Anything else defaults to English.

export type { Lang };

const STORAGE_KEY = 'studymind_lang';

function detectLang(): Lang {
  try {
    const nav = (navigator.language || 'en').toLowerCase();
    if (nav.startsWith('zh')) {
      // Hong Kong / Taiwan / Macau / explicit Hant -> Traditional; otherwise Simplified.
      if (/tw|hk|mo|hant/.test(nav)) return 'zhHant';
      if (/cn|sg|hans/.test(nav)) return 'zhHans';
      return 'zhHant'; // default Chinese variant: Traditional
    }
  } catch {
    /* ignore */
  }
  return 'en';
}

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string) => string;
  dict: TranslationDict;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function resolve(obj: TranslationDict, path: string): string | undefined {
  return path.split('.').reduce<unknown>((acc, k) => (acc as Record<string, unknown>)?.[k], obj) as
    | string
    | undefined;
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (saved && translations[saved]) return saved;
    } catch {
      /* ignore */
    }
    return detectLang();
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang === 'en' ? 'en' : lang === 'zhHant' ? 'zh-Hant' : 'zh-Hans';
    } catch {
      /* ignore */
    }
  }, [lang]);

  const setLang = (l: Lang) => setLangState(l);

  const t = (key: string): string => {
    const dict = translations[lang] || translations.en;
    const val = resolve(dict as TranslationDict, key) ?? resolve(translations.en as TranslationDict, key);
    return val ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, dict: translations[lang] || translations.en }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

// Small reusable language switcher.
export const LanguageSwitcher: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const { lang, setLang } = useLanguage();
  const options: { value: Lang; label: string }[] = [
    { value: 'en', label: 'English' },
    { value: 'zhHant', label: '繁體中文' },
    { value: 'zhHans', label: '简体中文' },
  ];
  return (
    <select
      value={lang}
      onChange={(e) => setLang(e.target.value as Lang)}
      aria-label="Language"
      style={{
        background: '#fff',
        color: '#111',
        border: '1px solid #ccc',
        borderRadius: 8,
        padding: '7px 8px',
        fontSize: '0.85rem',
        fontWeight: 600,
        cursor: 'pointer',
        ...style,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ color: '#111' }}>
          {o.label}
        </option>
      ))}
    </select>
  );
};
